/**
 * Agent Settings Service
 * Frontend service for managing OpenClaw agent connections and API keys.
 */

import { getApiBase } from '../config/api';
import { authFetch } from '../utils/authFetch';

export interface AgentConnection {
  id: string;
  user_id: string;
  name: string;
  provider: string;
  host: string;
  protocol: string;
  agent_name: string;
  gateway_token: string | null;
  is_active: boolean;
  last_connected_at: string | null;
  created_at: string;
  updated_at: string;
  icon: string;
  pairing_status: 'unpaired' | 'pending' | 'paired' | null;
  device_id: string | null;
  webhook_url: string | null;
  webhook_secret: string | null;
}

export interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  last_used_at: string | null;
  created_at: string;
  revoked_at: string | null;
}

export interface ApiKeyCreateResult extends ApiKey {
  key: string; // Full key, returned only once
}

export interface ConnectionTestResult {
  success: boolean;
  latencyMs?: number;
  error?: string;
}

class AgentSettingsService {
  private static instance: AgentSettingsService;
  private apiBase: string;

  private constructor() {
    this.apiBase = getApiBase();
  }

  static getInstance(): AgentSettingsService {
    if (!AgentSettingsService.instance) {
      AgentSettingsService.instance = new AgentSettingsService();
    }
    return AgentSettingsService.instance;
  }

  // --- Agent Connections ---

  async listConnections(): Promise<AgentConnection[]> {
    const res = await authFetch(`${this.apiBase}/api/agents`);
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to list connections');
    return res.json();
  }

  async createConnection(data: { name?: string; host?: string; provider?: string; agent_name?: string; icon?: string; webhook_url?: string }): Promise<AgentConnection> {
    const res = await authFetch(`${this.apiBase}/api/agents`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to create connection');
    return res.json();
  }

  async updateConnection(id: string, data: Partial<Pick<AgentConnection, 'name' | 'is_active' | 'agent_name' | 'icon' | 'webhook_url'>>): Promise<AgentConnection> {
    const res = await authFetch(`${this.apiBase}/api/agents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to update connection');
    return res.json();
  }

  async deleteConnection(id: string): Promise<void> {
    const res = await authFetch(`${this.apiBase}/api/agents/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to delete connection');
  }

  async testConnection(id: string): Promise<ConnectionTestResult> {
    const res = await authFetch(`${this.apiBase}/api/agents/${id}/test`, { method: 'POST' });
    if (!res.ok) throw new Error((await res.json()).error || 'Connection test failed');
    return res.json();
  }

  async regenerateToken(id: string): Promise<AgentConnection> {
    const res = await authFetch(`${this.apiBase}/api/agents/${id}/regenerate-token`, { method: 'POST' });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to regenerate token');
    return res.json();
  }

  async getActiveConnection(): Promise<AgentConnection | null> {
    const connections = await this.listConnections();
    return connections.find(c => c.is_active) || null;
  }

  // --- API Keys ---

  async listApiKeys(): Promise<ApiKey[]> {
    const res = await authFetch(`${this.apiBase}/api/apikeys`);
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to list API keys');
    return res.json();
  }

  async createApiKey(name?: string): Promise<ApiKeyCreateResult> {
    const res = await authFetch(`${this.apiBase}/api/apikeys`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to create API key');
    return res.json();
  }

  async revokeApiKey(id: string): Promise<void> {
    const res = await authFetch(`${this.apiBase}/api/apikeys/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to revoke API key');
  }
}

export default AgentSettingsService;
